import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { RefreshCw, Mail, Star, Activity } from 'lucide-react';
import { adminFetch, fmtNum, fmtPct, fmtRelative } from './api';
import {
  PageHeader, CountCard, Section, EmptyState, ErrorBlock, StatusPill,
  SearchInput, Pagination, ScrollTable, usePaginatedSearch,
} from './widgets';

// Per-kind labels for the three bucket counters (a/b/c). Mirrors what the
// agent CLI shows in its rich Progress bars so the dashboard view matches.
const RUN_KIND_LABELS = {
  analyze: {
    title: 'Analyze run',
    bucket_a: { label: 'found', cls: 'bg-emerald-500' },
    bucket_b: { label: 'no rating', cls: 'bg-amber-500' },
    bucket_c: { label: 'captcha / error', cls: 'bg-red-500' },
  },
  enrich: {
    title: 'Enrich run',
    bucket_a: { label: 'found email', cls: 'bg-emerald-500' },
    bucket_b: { label: 'no email', cls: 'bg-amber-500' },
    bucket_c: { label: 'errors', cls: 'bg-red-500' },
  },
};

const RUN_STATUS_TONES = {
  running: 'sent',
  completed: 'replied',
  failed: 'bounced',
};

const STATUS_TONES = {
  found: 'replied',
  not_found: 'neutral',
  captcha: 'bounced',
  timeout: 'followup',
  error: 'unsubscribed',
  '(none)': 'neutral',
};

export default function AdminPipeline() {
  const [data, setData] = useState(null);
  const [runs, setRuns] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [lastLoaded, setLastLoaded] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => { document.title = 'Sudco Admin — Enrich & analyze'; }, []);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const [d, r] = await Promise.all([
        adminFetch('/api/admin-stats/pipeline'),
        adminFetch('/api/admin-stats/pipeline-runs?limit=10'),
      ]);
      setData(d);
      setRuns(r.runs);
      setLastLoaded(new Date());
      setRefreshKey((k) => k + 1);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 5s while any run is active so the bars feel live
  // without the user clicking refresh.
  const hasActive = runs && runs.some((r) => r.status === 'running');
  useEffect(() => {
    if (!hasActive) return undefined;
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
  }, [hasActive, load]);

  if (error) return <ErrorBlock error={error} onRetry={load} />;
  if (!data) return <div className="text-text-muted">Loading…</div>;

  const e = data.enrichment;
  const a = data.analysis;
  const enrichRate = e.total ? (e.with_email / e.total) : null;
  const analyzeRate = a.total ? (a.with_rating / a.total) : null;

  return (
    <>
      <PageHeader
        title="Enrich & analyze"
        subtitle="Pipeline phase status and recent activity."
        right={
          <div className="flex items-center gap-3">
            {lastLoaded && (
              <span className="text-xs text-text-muted whitespace-nowrap">
                Updated {fmtRelative(lastLoaded.toISOString())}
              </span>
            )}
            <button
              onClick={load}
              disabled={loading}
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 bg-surface border border-border rounded-lg hover:bg-surface-2 disabled:opacity-60"
            >
              <RefreshCw size={12} className={loading ? 'animate-spin' : ''} />
              {loading ? 'Refreshing…' : 'Refresh'}
            </button>
          </div>
        }
      />

      <RunsSection runs={runs} />

      <Section title="Enrichment">
        <div className="flex items-center gap-2 mb-3 text-text-muted text-sm">
          <Mail size={14} />
          <span>Crawls each prospect's site for a contact email. <code className="text-xs">agent enrich</code></span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CountCard
            label="Enriched"
            n={e.total - e.never}
            sub={`${fmtPct(e.total ? (e.total - e.never) / e.total : null)} of ${fmtNum(e.total)} prospects`}
            accent
          />
          <CountCard label="With email" n={e.with_email} sub={`${fmtPct(enrichRate)} of all prospects`} />
          <CountCard
            label="No email found"
            n={e.no_email}
            sub={e.stale_no_email ? `${fmtNum(e.stale_no_email)} stale (>30d, retryable)` : null}
          />
          <CountCard label="Recent activity" n={e.last_24h} sub={`enriched in 24h · ${fmtNum(e.last_7d)} in 7d`} />
        </div>

        {e.never > 0 && (
          <div className="mt-3 text-xs text-text-muted">
            <span className="text-amber-400">{fmtNum(e.never)}</span> prospect{e.never === 1 ? '' : 's'} have never been enriched.
          </div>
        )}
      </Section>

      <Section title="Analysis">
        <div className="flex items-center gap-2 mb-3 text-text-muted text-sm">
          <Star size={14} />
          <span>Fetches Google Maps rating + top-5 review excerpts. <code className="text-xs">agent analyze</code></span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <CountCard
            label="Attempted"
            n={a.total - a.never}
            sub={`${fmtPct(a.total ? (a.total - a.never) / a.total : null)} of ${fmtNum(a.total)} prospects`}
            accent
          />
          <CountCard label="With rating" n={a.with_rating} sub={`${fmtPct(analyzeRate)} of all prospects`} />
          <CountCard label="With reviews" n={a.with_reviews} sub="top-5 excerpts cached" />
          <CountCard label="Recent activity" n={a.last_24h} sub={`attempted in 24h · ${fmtNum(a.last_7d)} in 7d`} />
        </div>

        {data.analysis_by_status.length > 0 && (
          <div className="mt-4 rounded-xl border border-border bg-surface p-4">
            <div className="text-xs uppercase tracking-wide text-text-muted mb-2">Last lookup outcome</div>
            <div className="flex flex-wrap gap-2">
              {data.analysis_by_status.map((s) => (
                <div
                  key={s.status}
                  className="inline-flex items-center gap-2 px-2.5 py-1 rounded-lg border border-border bg-surface-2"
                >
                  <StatusPill tone={STATUS_TONES[s.status] || 'neutral'}>{s.status}</StatusPill>
                  <span className="text-sm text-text font-medium">{fmtNum(s.count)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </Section>

      <RecentTableSection
        title="Recent enrichments"
        endpoint="/api/admin-stats/recent-enriched"
        refreshKey={refreshKey}
        emptyHint="Run `agent enrich` to populate this."
        columns={[
          { label: 'Business', render: (r) => (
            <Link to={`/admin/prospects/${r.id}`} className="text-text hover:text-accent whitespace-nowrap">
              {r.business_name || `Prospect #${r.id}`}
            </Link>
          ) },
          { label: 'Industry', render: (r) => <span className="text-text-muted whitespace-nowrap">{r.industry || '—'}</span> },
          { label: 'Email', render: (r) => (
            r.contact_email
              ? <span className="text-emerald-400 whitespace-nowrap">{r.contact_email}</span>
              : <span className="text-text-muted">— none</span>
          ) },
          { label: 'When', align: 'right', render: (r) => (
            <span className="text-text-muted text-xs whitespace-nowrap">{fmtRelative(r.enriched_at)}</span>
          ) },
        ]}
      />

      <RecentTableSection
        title="Recent analyses"
        endpoint="/api/admin-stats/recent-analyzed"
        refreshKey={refreshKey}
        emptyHint="Run `agent analyze` to populate this."
        columns={[
          { label: 'Business', render: (r) => (
            <Link to={`/admin/prospects/${r.id}`} className="text-text hover:text-accent whitespace-nowrap">
              {r.business_name || `Prospect #${r.id}`}
            </Link>
          ) },
          { label: 'Outcome', render: (r) => (
            <StatusPill tone={STATUS_TONES[r.rating_lookup_status] || 'neutral'}>
              {r.rating_lookup_status || '—'}
            </StatusPill>
          ) },
          { label: 'Rating', render: (r) => (
            r.rating != null
              ? <span className="text-text whitespace-nowrap">★ {r.rating} <span className="text-text-muted text-xs">({fmtNum(r.review_count || 0)})</span></span>
              : <span className="text-text-muted">—</span>
          ) },
          { label: 'Reviews', render: (r) => (
            r.has_reviews
              ? <span className="text-emerald-400 text-xs">cached</span>
              : <span className="text-text-muted text-xs">—</span>
          ) },
          { label: 'When', align: 'right', render: (r) => (
            <span className="text-text-muted text-xs whitespace-nowrap">{fmtRelative(r.last_rating_attempt)}</span>
          ) },
        ]}
      />
    </>
  );
}

function RunsSection({ runs }) {
  if (!runs) {
    return (
      <Section title="Live runs">
        <div className="text-text-muted text-sm">Loading…</div>
      </Section>
    );
  }
  const latestByKind = {};
  for (const r of runs) {
    if (!latestByKind[r.kind] || r.id > latestByKind[r.kind].id) {
      latestByKind[r.kind] = r;
    }
  }
  const ordered = Object.values(latestByKind).sort((a, b) => {
    const aRunning = a.status === 'running';
    const bRunning = b.status === 'running';
    if (aRunning !== bRunning) return aRunning ? -1 : 1;
    return b.id - a.id;
  });
  return (
    <Section title="Live runs">
      <div className="flex items-center gap-2 mb-3 text-text-muted text-sm">
        <Activity size={14} />
        <span>Real-time progress for in-flight <code className="text-xs">agent enrich</code> / <code className="text-xs">agent analyze</code> runs.</span>
      </div>
      {ordered.length === 0 ? (
        <EmptyState
          title="No runs reported yet"
          hint="Once you start an enrich or analyze run from the CLI, the agent will report progress here."
        />
      ) : (
        <div className="space-y-3">
          {ordered.map((r) => <RunCard key={r.id} run={r} />)}
        </div>
      )}
    </Section>
  );
}

function RunCard({ run }) {
  const labels = RUN_KIND_LABELS[run.kind] || RUN_KIND_LABELS.analyze;
  const total = Math.max(run.total || 0, 1);
  // Three segments of one bar — proportions are relative to the run total.
  const pctA = Math.min(100, ((run.bucket_a || 0) / total) * 100);
  const pctB = Math.min(100, ((run.bucket_b || 0) / total) * 100);
  const pctC = Math.min(100, ((run.bucket_c || 0) / total) * 100);
  const done = (run.bucket_a || 0) + (run.bucket_b || 0) + (run.bucket_c || 0);
  const overallPct = Math.min(100, (done / total) * 100);
  const tone = RUN_STATUS_TONES[run.status] || 'neutral';
  const meta = run.meta && typeof run.meta === 'object' ? run.meta : null;

  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 min-w-0">
          <Activity size={16} className="text-text-muted shrink-0" />
          <div className="min-w-0">
            <div className="font-medium text-text truncate capitalize">
              {labels.title}
            </div>
            <div className="text-xs text-text-muted">
              {fmtRelative(run.started_at)}
              {meta?.concurrency != null && ` · concurrency ${meta.concurrency}`}
              {meta?.delay_seconds != null && ` · delay ${meta.delay_seconds}s`}
              {run.skipped > 0 && ` · ${fmtNum(run.skipped)} skipped`}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-text-muted">
            {fmtNum(done)} / {fmtNum(run.total)} ({fmtPct(done / total)})
          </span>
          <StatusPill tone={tone}>{run.status}</StatusPill>
        </div>
      </div>

      {/* Stacked segmented bar — found / no-rating / problem proportions. */}
      <div className="mt-3 h-2 rounded-full bg-surface-2 overflow-hidden flex">
        <div className={`h-full transition-all duration-500 ${labels.bucket_a.cls}`} style={{ width: `${pctA}%` }} />
        <div className={`h-full transition-all duration-500 ${labels.bucket_b.cls}`} style={{ width: `${pctB}%` }} />
        <div className={`h-full transition-all duration-500 ${labels.bucket_c.cls}`} style={{ width: `${pctC}%` }} />
      </div>

      <div className="grid grid-cols-3 gap-2 mt-3 text-xs">
        <BucketStat
          color={labels.bucket_a.cls}
          label={labels.bucket_a.label}
          n={run.bucket_a}
          total={run.total}
        />
        <BucketStat
          color={labels.bucket_b.cls}
          label={labels.bucket_b.label}
          n={run.bucket_b}
          total={run.total}
        />
        <BucketStat
          color={labels.bucket_c.cls}
          label={labels.bucket_c.label}
          n={run.bucket_c}
          total={run.total}
        />
      </div>

      {/* Discreet end-of-run summary so completed runs still tell their story. */}
      {run.status !== 'running' && (
        <div className="mt-2 text-[11px] text-text-muted">
          {overallPct >= 100 ? 'Reached 100%' : `Stopped at ${fmtPct(done / total)}`}
          {' · '}{fmtRelative(run.finished_at || run.updated_at)}
        </div>
      )}
      {run.error && (
        <div className="mt-3 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">
          {run.error}
        </div>
      )}
    </div>
  );
}

function BucketStat({ color, label, n, total }) {
  const pct = total ? (Number(n || 0) / total) : null;
  return (
    <div className="rounded-lg border border-border bg-surface-2 px-2.5 py-1.5">
      <div className="flex items-center gap-1.5">
        <span className={`inline-block w-2 h-2 rounded-full ${color}`} />
        <span className="text-text-muted">{label}</span>
      </div>
      <div className="mt-0.5 text-text font-medium">
        {fmtNum(n || 0)} <span className="text-text-muted">({fmtPct(pct)})</span>
      </div>
    </div>
  );
}

function RecentTableSection({ title, endpoint, columns, emptyHint, refreshKey }) {
  const { query, setQuery, debounced, page, setPage, rows, total, loading, err, pageSize } =
    usePaginatedSearch({ endpoint, refreshKey });

  return (
    <Section
      title={title}
      action={<SearchInput value={query} onChange={setQuery} placeholder="Search business name…" />}
    >
      {err && (
        <div className="rounded-xl border border-red-500/30 bg-red-500/5 p-3 text-sm text-red-300 mb-3">
          {err.message}
        </div>
      )}
      {!rows ? (
        <div className="text-text-muted text-sm">Loading…</div>
      ) : rows.length === 0 ? (
        <EmptyState
          title={debounced ? 'No matches' : 'Nothing yet'}
          hint={debounced ? `No rows match "${debounced}".` : emptyHint}
        />
      ) : (
        <>
          <ScrollTable className={loading ? 'opacity-60' : ''}>
            <table className="w-full text-sm">
              <thead className="bg-surface-2 text-text-muted text-xs uppercase tracking-wide sticky top-0 z-10">
                <tr>
                  {columns.map((c) => (
                    <th
                      key={c.label}
                      className={`px-4 py-2.5 font-medium ${c.align === 'right' ? 'text-right' : 'text-left'}`}
                    >
                      {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((r) => (
                  <tr key={r.id} className="hover:bg-surface-2/40">
                    {columns.map((c) => (
                      <td key={c.label} className={`px-4 py-2.5 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                        {c.render(r)}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </ScrollTable>
          <Pagination
            page={page}
            pageSize={pageSize}
            total={total}
            onPage={setPage}
            loading={loading}
          />
        </>
      )}
    </Section>
  );
}
